/**
 * CC-003 / US-155 — Revenue & Conversion Funnel Dashboard (F-206).
 * T-155-001: funnel stage breakdown with conversion + drop-off.
 * T-155-002: cohort retention analysis and trend tracking alongside the funnel.
 * Admin-only data (LP requireAdmin) — renders empty/error states gracefully.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { growthAnalytics, FunnelData, Cohort } from '../../services/growthAnalytics';
import { LeadScoringSummary } from './LeadScoringSummary';

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

const defaultRange = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

export const ConversionFunnelDashboard: React.FC = () => {
    const [{ start, end }, setRange] = useState(defaultRange());
    const [funnel, setFunnel] = useState<FunnelData | null>(null);
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [f, c] = await Promise.all([
                growthAnalytics.getFunnel(start, end),
                growthAnalytics.getCohorts({ start, end, months: 6 }),
            ]);
            setFunnel(f);
            setCohorts(Array.isArray(c) ? c : []);
        } catch (e: any) {
            setError(e?.message || 'Unable to load funnel data');
            setFunnel(null);
            setCohorts([]);
        } finally {
            setLoading(false);
        }
    }, [start, end]);

    useEffect(() => { load(); }, [load]);

    const maxCount = funnel?.stages?.length ? Math.max(...funnel.stages.map((s) => s.count), 1) : 1;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="conversion-funnel-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Revenue &amp; Conversion Funnel</h1>
                <div className="flex items-center gap-2 text-sm">
                    <label htmlFor="funnel-start">From</label>
                    <input
                        id="funnel-start" type="date" value={start} className="border rounded px-2 py-1"
                        onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
                    />
                    <label htmlFor="funnel-end">To</label>
                    <input
                        id="funnel-end" type="date" value={end} className="border rounded px-2 py-1"
                        onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
                    />
                    <button onClick={load} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700">
                        Refresh
                    </button>
                </div>
            </div>

            {loading && <p className="text-gray-500" data-testid="funnel-loading">Loading funnel…</p>}
            {error && (
                <div className="bg-red-50 text-red-700 border border-red-200 rounded p-4 mb-6" data-testid="funnel-error">
                    {error}
                </div>
            )}

            {/* F-296 / T-232-002: lead scoring surfaced inside the growth dashboard.
                Self-hides while the scoring engine has no data. */}
            {!loading && !error && <LeadScoringSummary />}

            {!loading && !error && (!funnel || !funnel.stages?.length) && (
                <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center text-gray-500" data-testid="funnel-empty">
                    No funnel data for this period. Data appears once users start moving through signup stages.
                </div>
            )}

            {!loading && !error && funnel && !!funnel.stages?.length && (
                <>
                    <div className="bg-white rounded-xl shadow p-6 mb-6">
                        <p className="text-sm text-gray-500 mb-4">
                            Total users in period: <span className="font-semibold text-gray-800" data-testid="funnel-total">{funnel.totalUsers}</span>
                        </p>
                        <ul className="space-y-3" role="list" data-testid="funnel-stages">
                            {funnel.stages.map((stage) => (
                                <li key={stage.name} data-testid={`funnel-stage-${stage.name}`}>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="font-medium text-gray-700">{stage.name}</span>
                                        <span className="text-gray-500">
                                            {stage.count} · conv {pct(stage.conversionRate)} · drop {pct(stage.dropOff)}
                                        </span>
                                    </div>
                                    <div className="h-3 bg-gray-100 rounded overflow-hidden">
                                        <div
                                            className="h-3 bg-green-500 rounded"
                                            style={{ width: `${Math.max(2, (stage.count / maxCount) * 100)}%` }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {!!funnel.bySource?.length && (
                        <div className="bg-white rounded-xl shadow p-6 mb-6">
                            <h2 className="text-lg font-semibold mb-3">Conversion by source</h2>
                            <table className="w-full text-sm" data-testid="funnel-by-source">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                        <th className="py-2">Source</th>
                                        <th className="py-2 text-right">Signups</th>
                                        <th className="py-2 text-right">Committed</th>
                                        <th className="py-2 text-right">Conversion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {funnel.bySource.map((s) => (
                                        <tr key={s.source} className="border-b last:border-0">
                                            <td className="py-2">{s.source}</td>
                                            <td className="py-2 text-right">{s.signups}</td>
                                            <td className="py-2 text-right">{s.committed}</td>
                                            <td className="py-2 text-right">{pct(s.conversionRate)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {/* Cohort panel renders independently: an empty funnel range can
                still carry cohort history (and vice versa). */}
            {!loading && !error && (
                <div className="bg-white rounded-xl shadow p-6">
                        <h2 className="text-lg font-semibold mb-1">Cohort retention</h2>
                        <p className="text-sm text-gray-500 mb-3">Signup cohorts over time — how many remain active per month.</p>
                        {cohorts.length === 0 ? (
                            <p className="text-gray-500" data-testid="cohorts-empty">No cohort data for this period yet.</p>
                        ) : (
                            <div className="overflow-x-auto" data-testid="cohort-table">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b">
                                            <th className="py-2 pr-4">Cohort</th>
                                            <th className="py-2 pr-4 text-right">Size</th>
                                            {cohorts[0].retentionRate.map((_, i) => (
                                                <th key={i} className="py-2 px-2 text-right">M{i}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cohorts.map((c) => (
                                            <tr key={c.cohortMonth} className="border-b last:border-0">
                                                <td className="py-2 pr-4 font-medium">{c.cohortMonth}</td>
                                                <td className="py-2 pr-4 text-right">{c.size}</td>
                                                {c.retentionRate.map((r, i) => (
                                                    <td key={i} className="py-2 px-2 text-right" data-testid={`retention-${c.cohortMonth}-${i}`}>
                                                        {pct(r)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
            )}
        </div>
    );
};

export default ConversionFunnelDashboard;
